/**
 * Renders a single per-app Overview tile from a resolved
 * `OverviewTileValue` (count / revenue / cpa for one event_type +
 * source). Visually matches `ConfigurableKpi` but with a fixed metric
 * — no pencil affordance, no metric picker (the user configures these
 * in App Settings → Overview tiles instead).
 */

import type { ReactNode } from "react";
import { fmtInt, fmtMoney } from "@/lib/fmt";
import type { OverviewTileValue } from "@/lib/types";

function titleCaseFromEventType(s: string): string {
  // event_type strings are snake_case (Adapty/RC) or PascalCase (Adjust).
  // Split on _ / camel boundaries, capitalise each chunk. Best-effort —
  // power users can override via the editor's label field if they want
  // something specific.
  return s
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseDecimal(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
}

// ASA performance metrics use a fixed display label per machine key —
// keeps the editor + the tile in sync without round-tripping the
// catalog through the API. Add a new metric here as soon as the
// backend catalog grows (§39).
const ASA_METRIC_LABELS: Record<string, string> = {
  spend: "Spend",
  impressions: "Impressions",
  taps: "Taps",
  installs: "Installs",
  ctr: "CTR",
  cpm: "CPM",
  cpt: "Avg CPT",
  cpi: "CPI",
  ipm: "IPM",
};

function defaultLabel(tile: OverviewTileValue): string {
  if (tile.label && tile.label.trim()) return tile.label.trim();
  if (tile.event_source === "asa") {
    return ASA_METRIC_LABELS[tile.event_type] ?? tile.event_type;
  }
  const base = titleCaseFromEventType(tile.event_type);
  // Conversion ratio (§40): default label is "Numerator → Denominator".
  // Power-users can still override via the editor's label field.
  if (tile.metric_kind === "conversion" && tile.denominator_event_type) {
    const denomLabel =
      tile.denominator_event_source === "asa"
        ? ASA_METRIC_LABELS[tile.denominator_event_type] ??
          tile.denominator_event_type
        : titleCaseFromEventType(tile.denominator_event_type);
    return `${base} → ${denomLabel}`;
  }
  // Hint the metric kind so two tiles for the same event_type (e.g.
  // trial_started count + trial_started cpa) don't look identical.
  if (tile.metric_kind === "revenue") return `${base} Revenue`;
  if (tile.metric_kind === "cpa") return `${base} CPA`;
  return base;
}

// Unit applied to the displayed ASA metric value. Matches the
// catalog in `app_overview_tiles_service.ASA_METRICS`.
const ASA_METRIC_UNITS: Record<string, "$" | "%" | "int"> = {
  spend: "$",
  impressions: "int",
  taps: "int",
  installs: "int",
  ctr: "%",
  cpm: "$",
  cpt: "$",
  cpi: "$",
  ipm: "int",
};

export function PerAppTile({
  tile,
  className,
  overlay,
}: {
  tile: OverviewTileValue;
  // Extra classes (e.g. "hover-reveal") + absolutely-positioned overlay
  // nodes (pencil / trash affordances) for the inline Overview editor.
  className?: string;
  overlay?: ReactNode;
}) {
  const value = parseDecimal(tile.value);
  const delta = parseDecimal(tile.delta_pct);

  let displayValue: string;
  let unit: string | null = null;
  let suffix: string | null = null;
  if (value == null) {
    displayValue = "—";
  } else if (tile.event_source === "asa") {
    const asaUnit = ASA_METRIC_UNITS[tile.event_type] ?? "int";
    if (asaUnit === "$") {
      displayValue = fmtMoney(value);
      unit = "$";
    } else if (asaUnit === "%") {
      displayValue = value.toFixed(2);
      suffix = "%";
    } else {
      displayValue = fmtInt(value);
    }
  } else if (tile.metric_kind === "conversion") {
    // Backend already multiplies by 100; we just render two decimals.
    displayValue = value.toFixed(2);
    suffix = "%";
  } else if (tile.metric_kind === "count") {
    displayValue = fmtInt(value);
  } else {
    // revenue + cpa → USD with the leading $ marker rendered by the
    // <small> sibling, matching ConfigurableKpi's money tiles.
    displayValue = fmtMoney(value);
    unit = "$";
  }

  // Direction interpretation: count + revenue going UP is good (more
  // trials / more $$); CPA going UP is bad. Mirrors metrics.ts's
  // goodDir convention. delta is sent by the backend as a percentage
  // already — we just classify by sign. ASA cost metrics (spend OK
  // up; cpi/cpt/cpm bad up) follow the campaign-level Ads-Manager
  // semantics.
  let deltaDir: "pos" | "neg" | null = null;
  let deltaMoved: "up" | "down" | null = null;
  if (delta != null) {
    deltaMoved = delta >= 0 ? "up" : "down";
    const asaCostDownIsGood =
      tile.event_source === "asa" &&
      (tile.event_type === "cpi" ||
        tile.event_type === "cpt" ||
        tile.event_type === "cpm");
    const goodDir =
      tile.metric_kind === "cpa" || asaCostDownIsGood ? "down" : "up";
    deltaDir = deltaMoved === goodDir ? "pos" : "neg";
  }

  const fullLabel = defaultLabel(tile);
  return (
    <div className={["kpi", "kpi-perapp", className].filter(Boolean).join(" ")}>
      {overlay}
      <div className="kpi-label" title={fullLabel}>
        {fullLabel}
      </div>
      <div className="kpi-value">
        {unit === "$" && <small>$</small>}
        <span>{displayValue}</span>
        {suffix && <small>{suffix}</small>}
      </div>
      <div className="kpi-foot">
        {deltaDir && deltaMoved && delta != null ? (
          <>
            <span className={`delta ${deltaDir}`}>
              {deltaMoved === "up" ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
            </span>
            <span className="muted">vs prev period</span>
          </>
        ) : (
          <span className="muted">No previous values</span>
        )}
      </div>
    </div>
  );
}
